"use client";

import * as React from "react";
import { BottomSheet } from "./bottom-sheet";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import ShareIcon from "../icons/shareIcon";

export interface JobDetailsStats {
  label: string;
  value: string;
}

export interface JobDetailsSheetProps {
  isOpen: boolean;
  onClose: () => void;
  companyLogo?: string;
  companyName: string;
  jobTitle: string;
  location: string;
  postedAgo: string;
  expiryDate: string;
  earnings: string;
  validClicks: number;
  applications: number;
  experience: string;
  jobType: string;
  workMode: string;
  salary: string;
  epc: string;
  aboutRole: string;
  responsibilities: string[];
  aboutCompany: string;
  onShare?: () => void;
}

export function JobDetailsSheet({
  isOpen,
  onClose,
  companyLogo,
  companyName,
  jobTitle,
  location,
  postedAgo,
  expiryDate,
  earnings,
  validClicks,
  applications,
  experience,
  jobType,
  workMode,
  salary,
  epc,
  aboutRole,
  responsibilities,
  aboutCompany,
  onShare,
}: JobDetailsSheetProps) {
  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      showDragHandle
      contentClassName="fixed! bottom-0!"
      className="bg-[#FFF9EF] px-3 pb-4 pt-4 shadow-[0_-12px_76px_rgba(0,0,0,0.16)]"
    >
      <div className="space-y-4 break-words text-[#344054] font-plus-jakarta-sans">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 overflow-hidden rounded-md border border-black/10 bg-white">
            {companyLogo ? (
              <img src={companyLogo} alt={companyName} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs font-medium text-[#667085]">
                {companyName.charAt(0)}
              </div>
            )}
          </div>
          <span className="text-sm text-[#667085]">{companyName}</span>
        </div>

        <div className="space-y-3">
          <div>
            <p className="text-lg font-semibold text-[#344054]">{jobTitle}</p>
            <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-[#667085]">
              <span className="flex items-center gap-1 font-medium">
                <span className="inline-flex h-3 w-3 items-center justify-center rounded-full border border-black">
                  <span className="h-1.5 w-1.5 rounded-full bg-black" />
                </span>
                {location}
              </span>
              <span className="size-[3px] rounded-full bg-[#667085]" />
              <span>{postedAgo}</span>
              <span className="size-[3px] rounded-full bg-[#667085]" />
              <span className="text-[#FC6E16]">Expiry: {expiryDate}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-[10px] font-medium text-[#667085]">
            <span className="rounded border border-[#D1D1D6] bg-[#FBFBFB] px-2 py-1">Earnings: {earnings}</span>
            <span className="rounded border border-[#D1D1D6] bg-[#FBFBFB] px-2 py-1">
              Valid Clicks: {validClicks.toLocaleString()}
            </span>
            <span className="rounded border border-[#D1D1D6] bg-[#FBFBFB] px-2 py-1">
              Applications: {applications.toLocaleString()}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm font-medium text-[#344054]">
          {[
            { label: "Experience", value: experience },
            { label: "Job Type", value: jobType },
            { label: "Work Mode", value: workMode },
            { label: "Salary", value: salary },
            { label: "EPC", value: epc },
          ].map((item) => (
            <div key={item.label} className="flex flex-col text-xs text-[#667085]">
              <span>{item.label}</span>
              <span className="text-sm font-medium text-[#344054]">{item.value}</span>
            </div>
          ))}
        </div>

        <div className="space-y-3 text-sm text-[#344054]">
          <div>
            <p className="text-base font-semibold">About the Job</p>
            <div className="mt-2 space-y-3 text-sm text-[#667085]">
              <div>
                <p className="font-medium text-[#344054]">About the Role</p>
                <p className="mt-1 leading-relaxed">{aboutRole}</p>
              </div>
              <div>
                <p className="font-medium text-[#344054]">Responsibilities</p>
                <ul className="mt-1 list-disc space-y-1 pl-5 leading-relaxed">
                  {responsibilities.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-medium text-[#344054]">About the Company</p>
                <p className="mt-1 leading-relaxed">{aboutCompany}</p>
              </div>
            </div>
          </div>
        </div>

        <Button        
          onClick={onShare}
          size="lg"
          className={cn(
            "flex w-full items-center justify-center",
            "px-4 py-3 text-base font-semibold text-white shadow-[0_4px_0_0_#000]"
          )}
        >
          Share Job
          <ShareIcon />
        </Button>
      </div>
    </BottomSheet>
  );
}
